import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import {
  Smartphone, Upload, Link, Trash2, Star, RefreshCw,
  Download, Calendar, Package, Edit2, Check, X
} from 'lucide-react';
import './AppManagement.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes > 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  if (bytes > 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return bytes + ' B';
}

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function isBeta(v) {
  return /beta|alpha|rc/i.test(v || '');
}

export default function AppManagement() {
  const [versions, setVersions] = useState([]);
  const [settings, setSettings] = useState({ app_name: '', app_description: '', app_store_url: '', google_play_url: '' });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('versions'); // versions | upload | settings

  // Upload form
  const [uploadForm, setUploadForm] = useState({ version: '', version_name: '', description: '' });
  const [apkFile, setApkFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMsg, setUploadMsg] = useState(null);
  const fileRef = useRef();

  // Settings save
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState(null);

  // Inline edit
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/app/versions');
      if (res.data.success) {
        setVersions(res.data.versions || []);
        if (res.data.settings) {
          setSettings(prev => ({ ...prev, ...res.data.settings }));
        }
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Upload ──
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!apkFile) { setUploadMsg({ type: 'error', text: 'Выберите APK файл' }); return; }
    if (!uploadForm.version.trim()) { setUploadMsg({ type: 'error', text: 'Укажите версию' }); return; }

    setUploading(true);
    setUploadProgress(0);
    setUploadMsg(null);

    const fd = new FormData();
    fd.append('apk', apkFile);
    fd.append('version', uploadForm.version.trim());
    fd.append('version_name', uploadForm.version_name.trim());
    fd.append('description', uploadForm.description.trim());

    try {
      await api.post('/api/admin/app/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          if (ev.total) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        }
      });
      setUploadMsg({ type: 'success', text: 'APK успешно загружен!' });
      setUploadForm({ version: '', version_name: '', description: '' });
      setApkFile(null);
      if (fileRef.current) fileRef.current.value = '';
      setUploadProgress(0);
      loadData();
      setTimeout(() => setActiveTab('versions'), 1200);
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.response?.data?.error || 'Ошибка при загрузке' });
    }
    setUploading(false);
  };

  // ── Settings save ──
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    setSettingsMsg(null);
    try {
      await api.post('/api/admin/app/settings', settings);
      setSettingsMsg({ type: 'success', text: 'Настройки сохранены' });
      setTimeout(() => setSettingsMsg(null), 3000);
    } catch {
      setSettingsMsg({ type: 'error', text: 'Ошибка сохранения' });
    }
    setSavingSettings(false);
  };

  // ── Set latest ──
  const handleSetLatest = async (id) => {
    try {
      await api.put(`/api/admin/app/version/${id}`, { is_latest: true });
      loadData();
    } catch { alert('Ошибка'); }
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Удалить эту версию? APK файл будет удалён безвозвратно.')) return;
    try {
      await api.delete(`/api/admin/app/version/${id}`);
      loadData();
    } catch { alert('Ошибка удаления'); }
  };

  // ── Inline edit ──
  const startEdit = (v) => {
    setEditingId(v.id);
    setEditForm({ version: v.version, version_name: v.version_name || '', description: v.description || '' });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = async (id) => {
    try {
      await api.put(`/api/admin/app/version/${id}`, editForm);
      setEditingId(null);
      loadData();
    } catch { alert('Ошибка сохранения'); }
  };

  const latest = versions.find(v => !!v.is_latest);

  return (
    <div className="app-mgmt-page">
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-icon"><Smartphone size={22} /></div>
          <div>
            <h1 className="page-title">Приложение</h1>
            <p className="page-subtitle">Управление версиями APK и настройками</p>
          </div>
        </div>
        <button className="btn-refresh" onClick={loadData} disabled={loading}>
          <RefreshCw size={15} className={loading ? 'spin' : ''} />
          Обновить
        </button>
      </div>

      {/* ── Stats row ── */}
      <div className="stats-row">
        <div className="stat-card">
          <Package size={18} />
          <div>
            <div className="stat-num">{versions.length}</div>
            <div className="stat-label">Версий всего</div>
          </div>
        </div>
        <div className="stat-card">
          <Star size={18} />
          <div>
            <div className="stat-num">{latest ? latest.version_name || latest.version : '—'}</div>
            <div className="stat-label">Текущая версия</div>
          </div>
        </div>
        <div className="stat-card">
          <Download size={18} />
          <div>
            <div className="stat-num">{versions.reduce((s, v) => s + (v.download_count || 0), 0)}</div>
            <div className="stat-label">Всего скачиваний</div>
          </div>
        </div>
        <div className="stat-card">
          <Calendar size={18} />
          <div>
            <div className="stat-num">{latest ? fmtDate(latest.created_at) : '—'}</div>
            <div className="stat-label">Дата последней версии</div>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="tabs-nav">
        {[
          { key: 'versions', label: 'Версии', icon: <Package size={15} /> },
          { key: 'upload', label: 'Загрузить APK', icon: <Upload size={15} /> },
          { key: 'settings', label: 'Ссылки и описание', icon: <Link size={15} /> },
        ].map(t => (
          <button
            key={t.key}
            className={`tab-btn ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* ── VERSIONS TAB ── */}
      {activeTab === 'versions' && (
        <div className="tab-content">
          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : versions.length === 0 ? (
            <div className="empty-state">
              <Package size={40} />
              <p>Версии не загружены</p>
              <button className="btn btn-primary" onClick={() => setActiveTab('upload')}>
                <Upload size={14} /> Загрузить первую версию
              </button>
            </div>
          ) : (
            <div className="versions-table-wrap">
              <table className="versions-table">
                <thead>
                  <tr>
                    <th>Версия</th>
                    <th>Название</th>
                    <th>Описание</th>
                    <th>Размер</th>
                    <th>Скачиваний</th>
                    <th>Дата</th>
                    <th>Статус</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map(v => (
                    <tr key={v.id} className={v.is_latest ? 'row-latest' : ''}>
                      {editingId === v.id ? (
                        <>
                          <td><input className="cell-input" value={editForm.version} onChange={e => setEditForm(p=>({...p,version:e.target.value}))} /></td>
                          <td><input className="cell-input" value={editForm.version_name} onChange={e => setEditForm(p=>({...p,version_name:e.target.value}))} placeholder="Название" /></td>
                          <td><textarea className="cell-textarea" value={editForm.description} onChange={e => setEditForm(p=>({...p,description:e.target.value}))} rows={2} /></td>
                          <td>{fmtSize(v.apk_size)}</td>
                          <td>{v.download_count || 0}</td>
                          <td>{fmtDate(v.created_at)}</td>
                          <td></td>
                          <td className="actions-cell">
                            <button className="icon-btn success" onClick={() => saveEdit(v.id)} title="Сохранить"><Check size={14}/></button>
                            <button className="icon-btn" onClick={cancelEdit} title="Отмена"><X size={14}/></button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="ver-code">{v.version}</td>
                          <td>{v.version_name || <span className="text-muted">—</span>}</td>
                          <td className="desc-cell">{v.description || <span className="text-muted">—</span>}</td>
                          <td>{fmtSize(v.apk_size)}</td>
                          <td><span className="dl-count"><Download size={11}/>{v.download_count || 0}</span></td>
                          <td>{fmtDate(v.created_at)}</td>
                          <td>
                            {v.is_latest
                              ? <span className="badge badge-latest">Текущая</span>
                              : isBeta(v.version)
                                ? <span className="badge badge-beta">Beta</span>
                                : <span className="badge badge-archive">Архив</span>
                            }
                          </td>
                          <td className="actions-cell">
                            {v.apk_filename && (
                              <a className="icon-btn" href={`${API_URL}/api/app/download/${v.id}`} title="Скачать APK" target="_blank" rel="noreferrer">
                                <Download size={14}/>
                              </a>
                            )}
                            {!v.is_latest && (
                              <button className="icon-btn success" onClick={() => handleSetLatest(v.id)} title="Сделать текущей">
                                <Star size={14}/>
                              </button>
                            )}
                            <button className="icon-btn" onClick={() => startEdit(v)} title="Редактировать">
                              <Edit2 size={14}/>
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── UPLOAD TAB ── */}
      {activeTab === 'upload' && (
        <div className="tab-content">
          <form className="form-card" onSubmit={handleUpload}>
            <h3 className="form-title"><Upload size={16}/> Загрузить новую версию APK</h3>

            <div className="form-row two-col">
              <div className="form-group">
                <label>Версия <span className="req">*</span></label>
                <input
                  value={uploadForm.version}
                  onChange={e => setUploadForm(p => ({ ...p, version: e.target.value }))}
                  placeholder="например 1.2.0"
                  className="form-input"
                />
                <span className="form-hint">Технический номер: 1.0, 1.1, 2.0-beta и т.д.</span>
              </div>
              <div className="form-group">
                <label>Название версии</label>
                <input
                  value={uploadForm.version_name}
                  onChange={e => setUploadForm(p => ({ ...p, version_name: e.target.value }))}
                  placeholder="например «Обновление стриминга»"
                  className="form-input"
                />
                <span className="form-hint">Отображается в списке версий на сайте</span>
              </div>
            </div>

            <div className="form-group">
              <label>Описание изменений</label>
              <textarea
                value={uploadForm.description}
                onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Что нового в этой версии? Исправления, новые функции..."
                className="form-input"
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>APK / AAB файл <span className="req">*</span></label>
              <div
                className={`drop-zone ${apkFile ? 'has-file' : ''}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                onDragLeave={e => e.currentTarget.classList.remove('drag-over')}
                onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('drag-over');
                  const f = e.dataTransfer.files[0];
                  if (f) setApkFile(f);
                }}
              >
                {apkFile ? (
                  <div className="drop-file-info">
                    <Package size={28} />
                    <div>
                      <div className="drop-filename">{apkFile.name}</div>
                      <div className="drop-filesize">{fmtSize(apkFile.size)}</div>
                    </div>
                    <button type="button" className="icon-btn danger" onClick={e => { e.stopPropagation(); setApkFile(null); if (fileRef.current) fileRef.current.value = ''; }}>
                      <X size={14}/>
                    </button>
                  </div>
                ) : (
                  <div className="drop-placeholder">
                    <Upload size={32} />
                    <span>Перетащите .apk / .aab сюда или <strong>нажмите для выбора</strong></span>
                    <span className="drop-hint">Максимум 200 MB</span>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".apk,.aab"
                  style={{ display: 'none' }}
                  onChange={e => setApkFile(e.target.files[0] || null)}
                />
              </div>
            </div>

            {uploading && (
              <div className="progress-wrap">
                <div className="progress-bar-track">
                  <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
                <span>{uploadProgress}%</span>
              </div>
            )}

            {uploadMsg && (
              <div className={`alert ${uploadMsg.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                {uploadMsg.type === 'success' ? <Check size={14}/> : <X size={14}/>}
                {uploadMsg.text}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {uploading ? <><div className="spinner-sm"/>{uploadProgress}% загрузка…</> : <><Upload size={14}/> Загрузить APK</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── SETTINGS TAB ── */}
      {activeTab === 'settings' && (
        <div className="tab-content">
          <form className="form-card" onSubmit={handleSaveSettings}>
            <h3 className="form-title"><Link size={16}/> Ссылки на магазины и описание</h3>

            <div className="form-row two-col">
              <div className="form-group">
                <label>Название приложения</label>
                <input
                  value={settings.app_name || ''}
                  onChange={e => setSettings(p => ({ ...p, app_name: e.target.value }))}
                  placeholder="Ballet Post"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                {/* spacer */}
              </div>
            </div>

            <div className="form-group">
              <label>Описание приложения</label>
              <textarea
                value={settings.app_description || ''}
                onChange={e => setSettings(p => ({ ...p, app_description: e.target.value }))}
                placeholder="Краткое описание для страницы скачивания..."
                className="form-input"
                rows={3}
              />
            </div>

            <div className="form-row two-col">
              <div className="form-group">
                <label><span style={{marginRight:6}}>▶️</span> Google Play URL</label>
                <input
                  value={settings.google_play_url || ''}
                  onChange={e => setSettings(p => ({ ...p, google_play_url: e.target.value }))}
                  placeholder="https://play.google.com/store/apps/details?id=..."
                  className="form-input"
                />
                <span className="form-hint">Оставьте пустым, чтобы скрыть кнопку</span>
              </div>
              <div className="form-group">
                <label><span style={{marginRight:6}}>🍎</span> App Store URL</label>
                <input
                  value={settings.app_store_url || ''}
                  onChange={e => setSettings(p => ({ ...p, app_store_url: e.target.value }))}
                  placeholder="https://apps.apple.com/app/..."
                  className="form-input"
                />
                <span className="form-hint">Оставьте пустым, чтобы скрыть кнопку</span>
              </div>
            </div>

            {settingsMsg && (
              <div className={`alert ${settingsMsg.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                {settingsMsg.type === 'success' ? <Check size={14}/> : <X size={14}/>}
                {settingsMsg.text}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={savingSettings}>
                {savingSettings ? <><div className="spinner-sm"/>Сохранение…</> : <><Check size={14}/> Сохранить настройки</>}
              </button>
            </div>
          </form>

          {/* Preview */}
          <div className="preview-card">
            <h4 className="preview-title">Предпросмотр страницы</h4>
            <p className="preview-sub">Публичная страница: <a href="/app" target="_blank" rel="noreferrer">balletpost.com/app</a></p>
            <div className="preview-row">
              <div className="preview-btn preview-btn-primary">📱 Android APK</div>
              {settings.google_play_url
                ? <div className="preview-btn preview-btn-store">▶️ Google Play</div>
                : <div className="preview-btn preview-btn-disabled">▶️ Google Play <span>(не указан)</span></div>
              }
              {settings.app_store_url
                ? <div className="preview-btn preview-btn-store">🍎 App Store</div>
                : <div className="preview-btn preview-btn-disabled">🍎 App Store <span>(не указан)</span></div>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
